import { useEffect } from 'react';
import {
  getCriteriaFromGoalData,
  getPromptingTypeFromGoalData,
  getPromptingLevelFromGoalData,
} from '../utils/dataConversion';

export const useDataEntryEffects = ({
  allGoalData,
  prompting,
  subGoalIndex,
  criteria,
  setDataPoints,
  setCriteria,
  setPromptingType,
  setGoalPromptingLevel,
  setGoalNo,
  setExistingEntry,
  setExistingPromptingType,
  open,
  editingDataPoint,
  selectedDate,
  studentDataPoints,
  promptingType,
  transformRawDataToFormState,
  getPromptingStructure,
  convertCustomDataToCustomSection,
  processStudentGoalsData,
  studentGoalsData,
  goalNumber,
}) => {
  useEffect(() => {
    if (goalNumber && !editingDataPoint) {
      setGoalNo(goalNumber);
    }
  }, [goalNumber, editingDataPoint, setGoalNo]);
  // Effect for setting criteria and prompting
  useEffect(() => {
    const criteriaData = getCriteriaFromGoalData(allGoalData);
    setCriteria(criteriaData);

    const promptingData = getPromptingTypeFromGoalData(
      allGoalData,
      subGoalIndex,
      prompting
    );
    setPromptingType(promptingData);

    const promptingLevel = getPromptingLevelFromGoalData(allGoalData);
    setGoalPromptingLevel(promptingLevel);
  }, [
    allGoalData,
    prompting,
    subGoalIndex,
    setCriteria,
    setPromptingType,
    setGoalPromptingLevel,
  ]);

  // Effect for updating data points when criteria changes
  useEffect(() => {
    if (criteria && criteria.length > 0 && Array.isArray(criteria)) {
      setDataPoints((prev) =>
        prev.map((point) => ({
          ...point,
          criteria: criteria?.map((item) => ({
            MeasurementType: item.MeasurementType,
            Target: item.Target,
            TargetDuration: item.TargetDuration,
            Variable: item.Variable,
            PromptingLevel: item.PromptingLevel,
            value:
              point.criteria.find((c) => c.Variable === item.Variable)?.value ||
              0,
          })),
        }))
      );
    }
  }, [criteria, setDataPoints]);

  // Effect for editing data point
  useEffect(() => {
    if (open && editingDataPoint && allGoalData) {
      const goalNumber = editingDataPoint?.info?.goalNumber || null;
      setGoalNo(goalNumber);

      const pointsToEdit = editingDataPoint?.info?.data_points || [];
      if (pointsToEdit.length > 0) {
        const transformedData = transformRawDataToFormState(
          pointsToEdit,
          criteria,
          subGoalIndex,
          promptingType,
          allGoalData
        );

        setDataPoints(transformedData);
        setExistingEntry(editingDataPoint);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    editingDataPoint,
    setDataPoints,
    criteria,
    subGoalIndex,
    promptingType,
    allGoalData,
    setGoalNo,
    setExistingEntry,
  ]);

  // Effect for loading existing data when selectedDate changes
  useEffect(() => {
    if (editingDataPoint) {
      return;
    }
    if (selectedDate && studentDataPoints?.length) {
      const matchedDataEntries = studentDataPoints.filter(
        (dp) => dp.date === selectedDate
      );

      if (matchedDataEntries && matchedDataEntries.length > 0) {
        const goalNumber =
          matchedDataEntries[0]?.dataPoints?.info?.goalNumber || null;
        setGoalNo(goalNumber);

        let allDataPoints = [];

        matchedDataEntries.forEach((matchedData) => {
          if (matchedData.dataPoints?.info?.data_points?.length) {
            const existingDataPoint =
              matchedData.dataPoints.info.data_points[0];
            let detectedPromptingType = '';

            if (existingDataPoint.prompting) {
              if (
                existingDataPoint.prompting.pMinus !== undefined ||
                existingDataPoint.prompting.pPlus !== undefined
              ) {
                detectedPromptingType = 'P+ & P-';
              } else if (
                existingDataPoint.prompting.I !== undefined ||
                existingDataPoint.prompting.DVP !== undefined ||
                existingDataPoint.prompting.GP !== undefined
              ) {
                detectedPromptingType = 'IVP, DVP, PVP, GP, PP' ||
                  'I, VP (IVP, DVP, PVP), GP, PP, ViP, M';
              }
            }

            allDataPoints = [
              ...allDataPoints,
              ...matchedData?.dataPoints?.info?.data_points?.map(
                (dataPoint) => {
                  let criteriaData = [];

                  if (dataPoint.criteria && dataPoint.criteria.length > 0) {
                    criteriaData = dataPoint?.criteria?.map((criterion) => ({
                      ...criterion,
                      MeasurementType:
                        criterion.MeasurementType || criterion.measurementType,
                      Target: criterion.Target || criterion.target,
                      TargetDuration:
                        criterion.TargetDuration || criterion.measurement,
                      Variable: criterion.Variable || criterion.variable,
                    }));
                  } else if (criteria && criteria.length > 0) {
                    criteriaData = criteria?.map((item) => ({
                      MeasurementType: item.MeasurementType,
                      Target: item.Target,
                      TargetDuration: item.TargetDuration,
                      Variable: item.Variable,
                      value: 0,
                    }));
                  }

                  let promptingData = {};

                  if (dataPoint.prompting) {
                    promptingData = dataPoint.prompting;
                  } else {
                    promptingData = getPromptingStructure(
                      detectedPromptingType || promptingType
                    );
                  }

                  // For prompting goals, use prompting_measurement if available, otherwise use goal_value
                  // For other goals, use goal_value
                  const value = dataPoint.prompting_measurement !== undefined && dataPoint.prompting_measurement !== '' 
                    ? dataPoint.prompting_measurement 
                    : (dataPoint.goal_value || 'N/A');

                  return {
                    id: dataPoint.id,
                    value: value,
                    trialsNumber: dataPoint.trialsNumber || [0],
                    trailsValue: dataPoint.trials_value || {
                      TrailsMin: '',
                      TrailsMax: '',
                    },
                    selectedOptions: [
                      ...(dataPoint.abc_data ? ['Add ABC Data'] : []),
                      ...(dataPoint.phase_change
                        ? ['Add Phase Change Line']
                        : []),
                      ...(dataPoint.preceding_behavior
                        ? ['Add Preceding Behavior']
                        : []),
                      ...(dataPoint.event ? ['Add Event Line'] : []),
                      ...(dataPoint.timeOfCollection ||
                      dataPoint.timeOfCollectionSpecific
                        ? ['Add Time of Collection']
                        : []),
                      ...(dataPoint.promptingLevel
                        ? ['Add Prompting Level']
                        : []),
                      ...(dataPoint.sessionSummary
                        ? ['Add Session Summary']
                        : []),
                      ...(dataPoint.groupSize ? ['Add Group Size'] : []),
                      ...(dataPoint.sessionLength
                        ? ['Add Session Length']
                        : []),
                      ...(dataPoint.additionalStaff !== undefined
                        ? ['Add Staff Present Information']
                        : []),
                      // Add custom section options based on customData content
                      ...(() => {
                        const customSectionOptions = [];
                        const convertedCustomSection =
                          convertCustomDataToCustomSection(
                            dataPoint.customData,
                            allGoalData
                          );
                        convertedCustomSection.forEach((section) => {
                          customSectionOptions.push(section.docId);
                        });
                        return customSectionOptions;
                      })(),
                    ],
                    abcData:
                      typeof dataPoint.abc_data === 'object'
                        ? dataPoint.abc_data
                        : {
                            antecedent: '',
                            behavior: '',
                            consequence: '',
                            setting: '',
                            notes: '',
                          },
                    phaseChange: dataPoint.phase_change || '',
                    precedingBehavior: dataPoint.preceding_behavior || '',
                    eventLine: dataPoint.event || '',
                    timeOfCollection: dataPoint.timeOfCollection || '',
                    timeOfCollectionSpecific:
                      dataPoint.timeOfCollectionSpecific || '',
                    promptingLevel: dataPoint.promptingLevel || '',
                    notes: dataPoint.notes || '',
                    sessionSummary: dataPoint.sessionSummary || '',
                    groupSize: dataPoint.groupSize || '',
                    sessionLength: dataPoint.sessionLength || '',
                    cptCodes: dataPoint.cptCodes || '',
                    additionalStaff:
                      typeof dataPoint.additionalStaff === 'boolean'
                        ? dataPoint.additionalStaff
                        : dataPoint.additionalStaff === ''
                        ? ''
                        : false,
                    numberOfStaffs:
                      typeof dataPoint.numberOfStaffs === 'number'
                        ? dataPoint.numberOfStaffs
                        : 0,
                    prompting: promptingData,
                    criteria: criteriaData,
                    interventions: dataPoint.interventions || [],
                    customSection: convertCustomDataToCustomSection(
                      dataPoint.customData,
                      allGoalData
                    ),
                    currentGoal:
                      dataPoint.currentGoal ||
                      (subGoalIndex ? subGoalIndex : 'main'),
                  };
                }
              ),
            ];
          }
        });

        if (allDataPoints.length > 0) {
          setDataPoints(allDataPoints);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDate,
    studentDataPoints,
    promptingType,
    subGoalIndex,
    criteria,
    setDataPoints,
    editingDataPoint,
    allGoalData,
    getPromptingStructure,
    convertCustomDataToCustomSection,
  ]);

  // Effect for processing student goals data
  useEffect(() => {
    processStudentGoalsData();
  }, [studentGoalsData, processStudentGoalsData]);
};
